/**
 * Shared Authzilla BFF client via /api/gateway (cookie → Bearer).
 */

import { ROUTES } from "@/lib/constants";

export interface AuthApiCallOptions {
  endpoint: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  params?: Record<string, string>;
}

export class AuthApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
  }
}

/**
 * Turn Nest/Authzilla-style error payloads into a readable string.
 * Avoids UI showing "[object Object]".
 */
export function extractApiErrorMessage(
  payload: unknown,
  fallback = "API request failed",
): string {
  if (payload == null) return fallback;
  if (typeof payload === "string") return payload || fallback;
  if (typeof payload === "number" || typeof payload === "boolean") {
    return String(payload);
  }

  if (Array.isArray(payload)) {
    const parts = payload
      .map((item) => extractApiErrorMessage(item, ""))
      .filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : fallback;
  }

  if (typeof payload === "object") {
    const record = payload as Record<string, unknown>;

    // Support localization objects: { en: "English msg", ar: "Arabic msg" }
    if ("en" in record && typeof record.en === "string" && record.en.trim()) {
      return record.en;
    }

    // NestJS often uses message: string | string[] | Record<string, string>
    if ("message" in record) {
      const fromMessage = extractApiErrorMessage(record.message, "");
      if (fromMessage) return fromMessage;
    }

    // Sometimes error is a string ("Forbidden") or nested object
    if ("error" in record) {
      if (typeof record.error === "string" && record.error.trim()) {
        return record.error;
      }
      const fromError = extractApiErrorMessage(record.error, "");
      if (fromError && fromError !== fallback) return fromError;
    }

    if (typeof record.detail === "string" && record.detail.trim()) {
      return record.detail;
    }

    if (typeof record.title === "string" && record.title.trim()) {
      return record.title;
    }

    // If it has an errors array, extract those
    if ("errors" in record && Array.isArray(record.errors) && record.errors.length > 0) {
      const fromErrors = extractApiErrorMessage(record.errors, "");
      if (fromErrors) return fromErrors;
    }

    try {
      return JSON.stringify(payload);
    } catch {
      return fallback;
    }
  }

  return fallback;
}

export async function authApiCall<T = unknown>(
  options: AuthApiCallOptions,
): Promise<T> {
  const response = await fetch(ROUTES.AUTH.GATEWAY, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(options),
    credentials: "include",
  });

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = "/login";
      throw new Error("Session expired");
    }

    const errorBody = await response.json().catch(() => null);
    const message = extractApiErrorMessage(
      errorBody,
      `Request failed (${response.status})`,
    );
    throw new AuthApiError(message, response.status);
  }

  return response.json();
}
